import { TErrorCode, IFuriganaToken, IFuriganaGroup } from '@datatype/type';
import { SERVICE } from '@config/environment';
import * as color from '@constant/color';
import { BLANK_MARKER } from '@constant/variable';

// 하이픈(-)을 물결(~)로 치환. 예: "to-go" → "to~go"
export const replaceDashWithTilde = (text: string | undefined): string => (text ? text.replace(/-/g, '~') : '');

// 발음용 하이픈(-)을 공백으로 치환하고 trim. 예: "shi-jin" → "shi jin"
export const replaceDashForPronunciation = (text: string | undefined): string =>
  text ? text.replace(/-/g, ' ').trim() : '';

export const createAuthLogoText = () => {
  let authServiceText: string;

  switch (SERVICE) {
    case 'moashelf':
      authServiceText = 'Moashelf';
      break;
    case 'iyagigo':
      authServiceText = 'Iyagigo';
      break;
    case 'gridgio':
      authServiceText = 'GridgIO';
      break;
    default:
      authServiceText = '';
      break;
  }

  return authServiceText;
};

/**
 * @param date iso 8601 format string
 * @description iso 8601 날짜형식을 포맷팅
 */
export const formatDate = (date: string): string => {
  const formatMonthDay = (date: Date) => `${date.getMonth() + 1}월 ${date.getDate()}일`;
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const inputDate = new Date(date);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(inputDate, today)) return '오늘';
  if (isSameDay(inputDate, yesterday)) return '어제';

  return formatMonthDay(inputDate);
};

/**
 * @param iso 8601 format string ("2025-06-07T12:02:02.255785Z")
 * @description 2025.6.7 형태로 출력
 */
export const formatDotDate = (isoString: string): string => {
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 0부터 시작하므로 +1
  const day = date.getUTCDate();
  return `${year}.${month}.${day}`;
};

/**
 * @param iso 8601 format string ("2025-06-07T12:02:02.255785Z")
 * @param delimiter 구분자 (기본값: '', 예: '.' → 2025.06.07)
 * @description 20250607 형태로 출력
 */
export const formatNormalDate = (isoString: string, delimiter = ''): string => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}${delimiter}${month}${delimiter}${day}`;
};

/**
 * @description email의 @###.### 부분을 제거
 */
export const removeEmailDomain = (email: string) => {
  return email.match(/^[^@]+/)?.[0] ?? '';
};

export function formatEllipsis(text: string, length: number): string {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * @description 영어 소문자와 숫자로만 이루어진 아이디 유효성 검증 (6~16자리)
 */
export const getIsValidUsername = (userId: string): boolean => {
  if (['shelf', 'iyagi'].includes(userId)) return true;
  const regex = /^[a-z0-9]{6,16}$/;
  return regex.test(userId);
};

// https://help.naver.com/service/5640/contents/1036?lang=ko&osType=COMMONOS
/**
 * @description 비밀번호 유효성 검증 (8~16자, 영문 대소문자 + 숫자 + 지정 특수문자 포함)
 */
export const getIsValidPassword = (password: string): boolean => {
  const regex = /^(?=.*[a-zA-Z])[a-zA-Z\d!"#$%&'()*+,\-./:;<=>?@[₩\]^_`{|}~]{8,16}$/;
  return regex.test(password);
};

/**
 * @description 이메일 유효성 검증
 */
export const getIsValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * @description 인증코드 유효성 검증
 */
export const getIsValidAuthenticationCode = (code: string): boolean => {
  const regex = /^\d{6}$/;
  return regex.test(code);
};

/**
 * @description nickname 유효성 검증
 */
export const getIsValidNickname = (nickname: string, max: number): boolean => {
  const regex = new RegExp(`^[가-힣a-zA-Z0-9]{1,${max}}$`);
  return regex.test(nickname);
};

/**
 * @description hashtag 유효성 검증
 */
export const getIsValidHashtag = (hashtag: string): boolean => {
  const regex = /^#[\w가-힣]+(\/[\w가-힣]+){0,2}$/;
  return regex.test(hashtag);
};

/**
 * @description 문자열 색상코드
 */
export const getColorCode = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 70%, 50%)`;
};

/**
 * @description 접미사 제거
 */
export const removePrefix = (text: string, prefix: string) => {
  // replace(/^\[catch\]\s*/i, '');
  if (text.startsWith(prefix)) {
    return text.slice(prefix.length);
  }
  return text;
};

/**
 * @description error code에서 error text 생성
 */
export const createErrorText = (errorCode: TErrorCode) => {
  let errorText;
  switch (errorCode) {
    case 'invalid-username-format':
      errorText = `"아이디를 영문 소문자 및 숫자 조합"의 6-16 길이로 입력해주세요`;
      break;
    case 'invalid-password-format':
      errorText = '비밀번호를 "영문 대/소문자, 숫자 및 특수기호 조합"의 8-16 길이로 입력해주세요';
      break;
    case 'invalid-username-authentication':
      errorText = '아이디가 유효하지 않습니다.';
      break;
    case 'invalid-password-authentication':
      errorText = '비밀번호가 일치하지 않습니다.';
      break;
    case 'invalid-email-format':
      errorText = `이메일 형식이 아닙니다.`;
      break;
    case 'invalid-authentication-code':
      errorText = `인증코드가 유효하지 않습니다.`;
      break;
    case 'invalid-authentication-code-format':
      errorText = `인증코드는 6자리의 숫자입니다.`;
      break;
    case 'invalid-username-duplicate':
      errorText = `같은 아이디의 회원이 존재합니다.`;
      break;
    case 'invalid-password-modification':
      errorText = `현재 및 새 비밀번호가 동일합니다.`;
      break;
    case 'invalid-email-duplicate':
      errorText = `이미 등록되어 있는 이메일입니다.`;
      break;
    case 'invalid-authtype-oauth':
      errorText = `간편로그인 회원입니다.`;
      break;
    default:
      errorText = '';
  }

  return errorText;
};

export const createTestCategory = (testCode: string) => {
  switch (testCode) {
    // 문자·어휘
    case 'T1101':
    case 'T1201':
    case 'T1301':
    case 'T1401':
    case 'T1501':
      return '한자읽기';
    case 'T1202':
    case 'T1302':
    case 'T1402':
    case 'T1502':
      return '한자표기';
    case 'T1102':
    case 'T1204':
    case 'T1303':
    case 'T1403':
    case 'T1503':
      return '문맥규정';
    case 'T1103':
    case 'T1205':
    case 'T1304':
    case 'T1404':
    case 'T1504':
      return '유의표현';
    case 'T1104':
    case 'T1206':
    case 'T1305':
    case 'T1405':
      return '용법';
    // 문법
    case 'T1111':
    case 'T1211':
    case 'T1311':
    case 'T1411':
    case 'T1511':
      return '문법형식 판단';
    case 'T1112':
    case 'T1212':
    case 'T1312':
    case 'T1412':
    case 'T1512':
      return '문장 만들기';
    case 'T1113':
    case 'T1213':
    case 'T1313':
    case 'T1413':
    case 'T1513':
      return '글의 문법';
    default:
      return '';
  }
};

export const normalizeInstruction = (text?: string) => {
  if (!text) return '';
  return text
    .replace(/＿+/g, '＿＿＿') // ＿, ＿＿＿＿＿ -> ＿＿＿
    .replace(/（[\s]*）|（）/g, '（　　　）'); // （）, （     ） -> （   ）
};

export const normalizeQuestion = (test: 'jlpt' | 'toeic', text: string) => {
  if (!text) return '';
  const labelMap = { A: '1', B: '2', C: '3', D: '4', E: '5' };

  const spanStyle =
    'display:inline-flex;justify-content:center;align-items:center;' +
    'width:1.8em;height:1.1em;margin:0 0.3em;border:1.5px solid #000;border-radius:0px;' +
    'font-size:1em;line-height:1;';

  let normalized = '';

  if (test === 'jlpt') {
    normalized = text
      // [] [ ] ［］ ［ ］ () ( ) （） （ ） -> （   ）
      .replace(/[[［]\s*[\]］]|（\s*）|\(\s*\)/g, '（　　　）')
      // [hello] ［hello］ -> <u>hello</u>
      .replace(/[[［](.+?)[\]］]/g, (_, inner) => `<u>${inner}</u>`)
      // 'A' -> <span>A</span>
      .replace(/'([A-Za-z])'/g, (_, inner) => {
        const mapped = labelMap[inner.toUpperCase()] || inner;
        return `<span style="${spanStyle}">${mapped}</span>`;
      });

    // if (underline) normalized = `<u>${normalized}</u>`;
  } else if (test === 'toeic') {
    normalized = text
      // [] () （） __ -> -------
      .replace(/[[]\s*[\]]|（\s*）|\(\s*\)|__+/g, '-------')
      // [hello] -> <u>hello</u>
      .replace(/[[](.+?)[\]]/g, (_, inner) => `<u>${inner}</u>`);
  } else {
    normalized = '';
  }

  return normalized;
};

export const normalizeChoice = (test: 'jlpt' | 'toeic', texts: string[]) => {
  if (!texts) return [];
  return texts;
};

export const normalizePassage = (test: 'jlpt' | 'toeic', text: string) => {
  if (!text) return '';

  const labelMap = { A: '1', B: '2', C: '3', D: '4', E: '5' };

  if (test === 'jlpt') {
    const spanStyle =
      'display:inline-flex;justify-content:center;align-items:center;' +
      'width:1.8em;height:1.1em;margin:0 0.3em;border:1.5px solid #000;border-radius:0px;' +
      'font-size:1em;line-height:1;';

    // [A] ［A］-> <span>1<span>
    return text.replace(/[[［](.*?)[\]］]/g, (_, inner) => {
      const trimmed = inner.trim();
      const mapped = labelMap[trimmed.toUpperCase()] || trimmed;
      return `<span style="${spanStyle}">${mapped}</span>`;
    });
  } else if (test === 'toeic') {
    const spanStyle =
      'display:inline-flex;justify-content:center;align-items:center;' +
      'width:1.8em;height:1.1em;margin:0 0.3em;border-bottom:1.5px dashed #000;' +
      'font-size:1em;line-height:1;';

    // [A] -> <span>1<span>
    return text.replace(/\[(.*?)\]/g, (_, inner) => {
      const trimmed = inner.trim();
      const mapped = labelMap[trimmed.toUpperCase()] || trimmed;
      return `<span style="${spanStyle}">${mapped}</span>`;
    });
  } else {
    return '';
  }
};

/**
 * 빈칸문제 텍스트에서 [A] 패턴을 빈칸 마커(BLANK_MARKER)로 감싼다. 스타일링은 TextBlock이 담당.
 *
 * 입력: "私は韓国という[国]に住んでいます。"
 * 결과: "私は韓国という###国###に住んでいます。"
 */
export const markBlanks = (text: string): string => {
  if (!text) return '';
  return text.replace(/\[(.+?)\]/g, (_, inner) => `${BLANK_MARKER}${inner}${BLANK_MARKER}`);
};

/**
 * @deprecated TextBlock이 빈칸 처리를 직접 담당하도록 마이그레이션됨. `markBlanks` 사용.
 *
 * 빈칸문제 텍스트에서 [A] 패턴을 HTML <span>으로 변환한다.
 *
 * === 동작 예시 ===
 *
 * 입력: "私は韓国という[国]に住んでいます。"
 *
 * blank=true, color=false (문제 풀기 전):
 *   → "私は韓国という<span style="...bg:white;color:transparent;">国</span>に住んでいます。"
 *   → [국] 부분이 흰색 박스로 보이고 텍스트는 투명 (답 가림)
 *
 * blank=true, color=true (정답 확인 후):
 *   → "私は韓国という<span style="...bg:white;color:green;">国</span>に住んでいます。"
 *   → [국] 부분이 흰색 박스 안에 초록색 텍스트로 표시 (답 노출)
 *
 * blank=false (기본):
 *   → "私は韓国という<span style="font-weight:800;color:green;">国</span>に住んでいます。"
 *   → [국] 부분이 굵은 초록색 텍스트로 표시
 */
export const normalizeCloze = (
  text: string,
  { blank, textColor, underlineColor }: { blank?: boolean; textColor?: string; underlineColor?: string } = {},
) => {
  if (!text) return '';
  const blankSpanStyle =
    'display:inline;' +
    `margin:0 0.2em;padding:0 0.4em;border:1.5px solid #ffffff;border-radius:5px;${underlineColor ? `border-bottom:1.5px solid ${underlineColor};border-radius:0;` : ''}` +
    `background-color:#ffffff;color:${textColor ?? 'transparent'};` +
    'font-size:1em;line-height:1;';

  const plainSpanStyle = `font-weight:800;color:${textColor ?? color.GREEN_4};${underlineColor ? `border-bottom:1.5px solid ${underlineColor};` : ''}`;

  let normalized = '';

  if (blank) {
    normalized = text
      // [hello] -> <span>hello</span>
      .replace(/[[](.+?)[\]]/g, (_, inner) => `<span style="${blankSpanStyle}">${inner}</span>`);
  } else {
    normalized = text
      // [hello] -> <span>hello</span>
      .replace(/[[](.+?)[\]]/g, (_, inner) => `<span style="${plainSpanStyle}">${inner}</span>`);
  }

  return normalized;
};

export const normalizeExplanation = (text?: string, textColor = color.BLACK_2, isBold = true) => {
  if (!text) return '';

  let normalized = text;
  // 작은따옴표 안의 문구를 지정 색상과 굵기로 강조한다. textColor='inherit'이면 부모 색상을 유지한다.
  normalized = normalized.replace(
    // 영어 단어 내부 apostrophe(don't, student's)는 구분자가 아니지만,
    // 작은따옴표로 감싼 영어 표현('don't', 'very good')은 강조한다.
    /(?<![A-Za-z])'((?:[^']|(?<=[A-Za-z])'(?=[A-Za-z]))+)'/g,
    `<span style="color: ${textColor};${isBold ? ' font-weight:bold;' : ''}">$1</span>`,
  );

  return normalized;
};

export const sanitizeSentencePronunciation = (sentencePronunciation: string, answerPronunciation: string[]) => {
  if (!sentencePronunciation) return '';
  if (!answerPronunciation || answerPronunciation.length === 0) return sentencePronunciation;

  let normalized = sentencePronunciation;

  answerPronunciation.forEach((keyword) => {
    if (keyword) {
      const highlighted = `<span style="color: ${color.GREEN_4}; font-weight: normal">${keyword}</span>`;
      normalized = normalized.replace(new RegExp(keyword, 'g'), highlighted);
    }
  });

  return normalized;
};

/**
 * @description 문장 템플릿에 배열 값을 순서대로 채워넣기
 * @param template 템플릿 문자열 (예: "그 [A]는 공원에서 [B]을 합니다.")
 * @param answers 채울 값들의 배열 (예: ["여자", "산책"])
 * @param braket 대괄호 유지 여부 (기본값: false)
 * @returns 완성된 문장
 * @example
 * completeSentence("그 [A]는 공원에서 [B]을 합니다.", ["여자", "산책"])
 * // => "그 여자는 공원에서 산책을 합니다."
 *
 * completeSentence("그 [A]는 공원에서 [B]을 합니다.", ["여자", "산책"], true)
 * // => "그 [여자]는 공원에서 [산책]을 합니다."
 */
export const completeSentence = (template: string, answers: string[], braket = false): string => {
  if (!template) return '';
  if (!answers || answers.length === 0) return template;

  const PLACE_HOLDERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  let sentence = template;

  answers.forEach((value, index) => {
    if (index < PLACE_HOLDERS.length) {
      const placeholder = PLACE_HOLDERS[index];
      const regex = new RegExp(`\\[${placeholder}\\]`, 'g');
      const replacement = braket ? `[${value}]` : value;
      sentence = sentence.replace(regex, replacement);
    }
  });

  return sentence;
};

// ############### furigana ###############

/**
 * @deprecated TextBlock의 `annotation` prop으로 마이그레이션됨. 신규 코드에서는 사용 금지.
 *             HTML 합성 레거시(cloze-explanation, conversation-board 등)에만 잔존.
 *
 * 완전한 일본어 문장에서 한자↔히라가나 매핑(furigana map)을 생성한다.
 * 같은 한자가 여러 번 등장하면 배열에 순서대로 저장한다.
 *
 * 입력:
 *   sentence: "私は韓国という国に住んでいます。"
 *   reading:  "わたしはかんこくというくににすんでいます。"
 *
 * 결과:
 *   Map { "私" → ["わたし"], "韓国" → ["かんこく"], "国" → ["くに"], "住" → ["す"] }
 */
export const createFuriganaMap = (sentence: string, reading: string): Map<string, string[]> => {
  let tokens = parseFurigana(sentence, reading);
  // 표기와 reading 양쪽에 쉼표·마침표가 있으면 공백 경계가 중복되어 매칭이 실패할 수 있다.
  // 한자 토큰을 하나도 찾지 못한 경우에만 구두점을 제외한 뒤 한 번 더 정렬한다.
  if (!tokens.some((token) => token.ruby)) {
    const withoutPunctuation = (text: string) => text.replace(/[、。！？,!?]/g, '');
    tokens = parseFurigana(withoutPunctuation(sentence), withoutPunctuation(reading));
  }
  const map = new Map<string, string[]>();
  for (const t of tokens) {
    if (!t.ruby) continue;
    const list = map.get(t.text) || [];
    list.push(t.ruby);
    map.set(t.text, list);
  }
  return map;
};

/**
 * @deprecated TextBlock의 `annotation` prop으로 마이그레이션됨. 신규 코드에서는 사용 금지.
 *             HTML 합성 레거시(cloze-explanation, conversation-board 등)에만 잔존.
 *
 * 문자열에서 furigana map에 해당하는 문자열을 ruby 태그로 교체한다.
 * HTML 태그가 포함되어 있으면 태그는 건드리지 않고 텍스트 노드만 처리한다.
 *
 * 입력:
 *   text: '私は<span>国</span>に住んでいます。'
 *   map:  Map { "私"→["わたし"], "国"→["くに"], "住"→["す"] }
 *
 * 결과:
 *   '<ruby>私<rt>わたし</rt></ruby>は...'
 */
export const addFurigana = (
  text: string,
  furiganaMap: Map<string, string[]>,
  rubyAlign: 'start' | 'center' | 'space-around' = 'space-around',
): string => {
  // map 복제 (shift로 소비하므로 원본 보호)
  const consumeMap = new Map<string, string[]>();
  Array.from(furiganaMap.entries()).forEach(([key, values]) => {
    consumeMap.set(key, [...values]);
  });
  // 긴 키부터 매칭 (韓国 vs 国 → 韓国 우선)
  const keys = Array.from(consumeMap.keys()).sort((a, b) => b.length - a.length);

  // HTML 태그는 그대로, 텍스트 노드만 ruby 변환
  return text.replace(/((<[^>]+>)|([^<]+))/g, (_match, _full, tag, text) => {
    if (tag) return tag;

    let result = '';
    let i = 0;
    while (i < text.length) {
      let matched = false;
      for (const key of keys) {
        const values = consumeMap.get(key)!;
        if (values.length === 0) continue;
        if (text.substring(i, i + key.length) === key) {
          const alignStyle = `ruby-align:${rubyAlign};`;
          result += `<ruby style="${alignStyle}">${key}<rp>(</rp><rt style="font-size:0.6em;color:inherit">${values.shift()}</rt><rp>)</rp></ruby>`;
          i += key.length;
          matched = true;
          break;
        }
      }
      if (!matched) {
        result += text[i];
        i++;
      }
    }
    return result;
  });
};

// ############### parseFurigana 내부 로직 ###############

const getIsKanji = (ch: string): boolean => {
  const code = ch.charCodeAt(0);
  return (code >= 0x4e00 && code <= 0x9faf) || (code >= 0x3400 && code <= 0x4dbf) || code === 0x3005;
};

const getIsKatakana = (ch: string): boolean => {
  const code = ch.charCodeAt(0);
  return code >= 0x30a0 && code <= 0x30ff;
};

const getIsNumber = (ch: string): boolean => ch >= '0' && ch <= '9';

const needsRuby = (ch: string): boolean => getIsKanji(ch) || getIsKatakana(ch) || getIsNumber(ch);

const katakanaToHiragana = (str: string): string =>
  str
    .split('')
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (getIsKatakana(ch) && code !== 0x30fc) return String.fromCharCode(code - 0x60);
      return ch;
    })
    .join('');

/**
 * 한자 포함 문장과 히라가나 읽기 문장을 비교하여 한자↔히라가나 토큰 배열을 반환한다.
 *
 * === 동작 예시 ===
 *
 * 입력:
 *   sentence: "私は韓国という国に住んでいます。"
 *   reading:  "わたしはかんこくというくににすんでいます。"
 *
 * 1단계 - tokenize:
 *   [私](ruby) [は](anchor) [韓国](ruby) [という](anchor) [国](ruby) [に](anchor) [住](ruby) [んでいます。](anchor)
 *
 * 2단계 - 재귀적 백트래킹(matchTokens):
 *   私→"わたし", は→소거, 韓国→"かんこく", という→소거, 国→"くに", に→소거, 住→"す", んでいます。→소거
 *
 * 결과:
 *   [{ text:"私", ruby:"わたし" }, { text:"は" }, { text:"韓国", ruby:"かんこく" }, ...]
 */
export const parseFurigana = (sentence: string, reading: string): IFuriganaToken[] => {
  if (!sentence || !reading) return [];

  // 특수문자(숫자, 영문, 구두점 등)를 공백으로 치환 → tokenize에서 한자 그룹이 합쳐지지 않도록 경계 보존
  // 구두점, 영문, 특수기호 등을 제거하고 일본어와 숫자만 남긴다
  // 히라가나(3040-309F) + 카타카나(30A0-30FF) + CJK한자(4E00-9FAF, 3400-4DBF) + 반복부호(々) + 장음(ー) + 숫자(0-9)
  const clean = (s: string) =>
    s.replace(/[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u3400-\u4DBF\u3005\u30FC0-9]/g, ' ');
  const cleanedSentence = clean(sentence);
  const cleanedReading = clean(reading);

  const groups = tokenize(cleanedSentence);
  if (groups.every((g) => !g.needsRuby)) return [{ text: sentence }];

  // 공백(특수문자 치환분)은 sentence/reading 한쪽에만 있을 수 있음.
  // - anchor의 공백: reading 쪽 공백이 있으면 같이 소비, 없으면 skip
  // - ruby: 공백을 포함하지 않음 (reading 공백을 만나면 거기서 ruby 종료 → 경계로 활용)
  // - 비공백 anchor: 매칭 전 reading 공백 skip
  const stripSpace = (s: string) => s.replace(/ /g, '');

  const getRemainingLength = (fromIdx: number): number => {
    let len = 0;
    for (let i = fromIdx; i < groups.length; i++) len += stripSpace(groups[i].text).length;
    return len;
  };

  // reading에서 readPos부터 공백을 건너뛴 위치 반환
  const skipSpaces = (readPos: number): number => {
    let p = readPos;
    while (p < cleanedReading.length && cleanedReading[p] === ' ') p++;
    return p;
  };

  // 한자↔reading 정렬은 형태소 분석 없이는 모호하다(예: くにす → 国(くに)|住(す) vs 国(く)|住(にす)).
  // greedy/lazy 단일 방식은 서로 다른 케이스에서 깨지므로, 모든 정합 해를 수집한 뒤 점수로 최선을 고른다.
  // 점수 규칙:
  //   1) 과식 페널티: ruby 한 글자(한자)당 음수가 3을 초과하면 페널티 → 명백한 과식 제거 (예: 幼=おさないあ)
  //   2) tie면 greedy 선호: 앞쪽 한자가 더 많이 먹는 해 선택 → 同자 중복 경계 케이스 해결 (예: 母=はは, 国=くに)
  const solutions: IFuriganaToken[][] = [];

  /** 재귀적 백트래킹으로 그룹과 reading의 모든 정합 해를 solutions에 수집. */
  const collectSolutions = (groupIdx: number, readPos: number, acc: IFuriganaToken[]): void => {
    if (groupIdx >= groups.length) {
      if (skipSpaces(readPos) === cleanedReading.length) solutions.push([...acc]);
      return;
    }

    const group = groups[groupIdx];

    if (!group.needsRuby) {
      const anchorIsSpaceOnly = stripSpace(group.text).length === 0;
      if (anchorIsSpaceOnly) {
        // 공백 anchor: reading의 공백을 소비(있으면 강한 경계, 없으면 skip)
        collectSolutions(groupIdx + 1, skipSpaces(readPos), [...acc, { text: group.text }]);
        return;
      }
      const startPos = skipSpaces(readPos);
      const anchor = stripSpace(katakanaToHiragana(group.text));
      if (cleanedReading.substring(startPos, startPos + anchor.length) !== anchor) return;
      collectSolutions(groupIdx + 1, startPos + anchor.length, [...acc, { text: group.text }]);
      return;
    }

    // ruby 매칭: reading 공백을 만나면 그 위치까지로 ruby 후보 제한 (경계 활용)
    const startPos = skipSpaces(readPos);
    const nextSpacePos = (() => {
      let p = startPos;
      while (p < cleanedReading.length && cleanedReading[p] !== ' ') p++;
      return p;
    })();
    // 숫자는 표기 자릿수보다 읽기가 짧을 수 있다. 예: 1000 → せん.
    // 자릿수를 최소 길이로 쓰면 해당 요미가나를 매칭하지 못해 누락된다.
    const minLen = /^[0-9]+$/.test(group.text) ? 1 : group.text.length;
    const segmentLen = nextSpacePos - startPos;
    const tailNoSpace = stripSpace(cleanedReading.substring(nextSpacePos)).length;
    const remainingAfterThis = getRemainingLength(groupIdx + 1);
    // 이 ruby가 가져갈 수 있는 최대 = 다음 경계까지 길이와, 전체 잔여에서 다음 그룹들 몫 뺀 값 중 작은 값
    const maxLen = Math.min(segmentLen, segmentLen + tailNoSpace - remainingAfterThis);

    for (let len = minLen; len <= maxLen; len++) {
      const annotation = cleanedReading.substring(startPos, startPos + len);
      collectSolutions(groupIdx + 1, startPos + len, [...acc, { text: group.text, ruby: annotation }]);
    }
  };

  collectSolutions(0, 0, []);
  if (solutions.length === 0) return [{ text: sentence }];

  // 과식 페널티: ruby 그룹의 (음수 / 한자수)가 3을 초과한 만큼 합산
  const getOvereatPenalty = (sol: IFuriganaToken[]): number => {
    let penalty = 0;
    for (const t of sol) {
      if (!t.ruby) continue;
      const kanjiCount = stripSpace(t.text).length || 1;
      const per = t.ruby.length / kanjiCount;
      if (per > 3) penalty += per - 3;
    }
    return penalty;
  };

  // tie-break(greedy): ruby 길이를 앞에서부터 사전식 비교, 앞쪽 한자가 더 많이 먹는 해를 선호
  const compareSolutions = (a: IFuriganaToken[], b: IFuriganaToken[]): number => {
    const pa = getOvereatPenalty(a);
    const pb = getOvereatPenalty(b);
    if (pa !== pb) return pa - pb;
    const ra = a.filter((t) => t.ruby).map((t) => (t.ruby as string).length);
    const rb = b.filter((t) => t.ruby).map((t) => (t.ruby as string).length);
    for (let i = 0; i < Math.min(ra.length, rb.length); i++) {
      if (ra[i] !== rb[i]) return rb[i] - ra[i];
    }
    return 0;
  };

  const matched = [...solutions].sort(compareSolutions)[0];

  // ===== 이전 구현 (기록 보존): 첫 성공 해를 바로 반환하는 단일 백트래킹 =====
  // greedy(긴 것부터)는 幼い間 → 幼=おさないあ 과식, lazy(짧은 것부터)는 国に住 → 国=く 부족 문제가 있었다.
  // const matchTokens = (groupIdx: number, readPos: number): IFuriganaToken[] | null => {
  //   if (groupIdx >= groups.length) return skipSpaces(readPos) === cleanedReading.length ? [] : null;
  //   const group = groups[groupIdx];
  //   if (!group.needsRuby) {
  //     const anchorIsSpaceOnly = stripSpace(group.text).length === 0;
  //     if (anchorIsSpaceOnly) {
  //       const next = skipSpaces(readPos);
  //       const rest = matchTokens(groupIdx + 1, next);
  //       return rest ? [{ text: group.text }, ...rest] : null;
  //     }
  //     const startPos = skipSpaces(readPos);
  //     const anchor = stripSpace(katakanaToHiragana(group.text));
  //     if (cleanedReading.substring(startPos, startPos + anchor.length) !== anchor) return null;
  //     const rest = matchTokens(groupIdx + 1, startPos + anchor.length);
  //     return rest ? [{ text: group.text }, ...rest] : null;
  //   }
  //   const startPos = skipSpaces(readPos);
  //   ... (ruby 길이 루프: greedy는 maxLen→minLen, lazy는 minLen→maxLen)
  //   return null;
  // };
  // const matched = matchTokens(0, 0);
  // ============================================================================

  if (!matched) return [{ text: sentence }];

  // 특수문자 치환용 공백을 결과에서 제거
  // - 공백만으로 이루어진 anchor 토큰은 삭제
  // - 텍스트 내 공백은 제거
  return matched
    .map((t) => (t.ruby ? t : { ...t, text: t.text.replace(/ /g, '') }))
    .filter((t) => t.ruby || t.text.length > 0);
};

/**
 * 순수 일본어 문장을 [한자/카타카나 구간]과 [히라가나 구간]으로 분할한다.
 * 같은 타입의 연속 문자는 하나의 그룹으로 합친다.
 * (특수문자는 parseFurigana에서 미리 제거되므로 여기에는 들어오지 않는다)
 *
 * "私は韓国という国に住んでいます"
 * → [私](ruby) [は](anchor) [韓国](ruby) [という](anchor) [国](ruby) [に](anchor) [住](ruby) [んでいます](anchor)
 */
const tokenize = (sentence: string): IFuriganaGroup[] => {
  const groups: IFuriganaGroup[] = [];
  let i = 0;

  while (i < sentence.length) {
    const rubyNeeded = needsRuby(sentence[i]);
    let text = sentence[i];
    i++;

    while (i < sentence.length && needsRuby(sentence[i]) === rubyNeeded) {
      text += sentence[i];
      i++;
    }

    groups.push({ text, needsRuby: rubyNeeded });
  }

  return groups;
};
